import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, ChevronLeft, Users2, MapPin, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import StepEditor from '../crm/StepEditor';
import { api } from '../../lib/api';
import { CONTACT_ROLE_OPTIONS, contactRoleLabel } from '../../lib/outreach';

// CampaignWizard — Sprint K replacement for the tiny "new campaign" modal.
// Walks the operator through five sequential steps so first-time setup
// can't end with an active-but-broken campaign:
//
//   1. Basics       — name + description + sender (creates the draft row)
//   2. Targeting    — stages, borough/district/type, recipient role
//                     + live "X eligible accounts" + expandable WHICH list
//   3. Steps        — drip email sequence (delegates to StepEditor)
//   4. Recipients   — auto-enroll (cron) OR manual enrollment redirect
//   5. Review       — summary + Activate (status draft → active)
//
// After step 1 the campaign exists as a draft so partial work survives
// refreshes / accidental closes — re-opening just resumes editing the
// same row via the regular detail page panels (the wizard isn't the
// only edit surface, just the friendliest one for first-time setup).

const STAGES = ['prospect', 'contacted', 'engaged', 'verbal_yes', 'contracted', 'maintained', 'dormant', 'closed_lost'];
// NYC DOE uses single-letter borough codes — stored values stay the codes
// to match the existing SQL filter (`a.type_school_borough = ?`).
const BOROUGHS = [
  { code: '',  label: 'Any' },
  { code: 'M', label: 'Manhattan' },
  { code: 'X', label: 'Bronx' },
  { code: 'K', label: 'Brooklyn' },
  { code: 'Q', label: 'Queens' },
  { code: 'R', label: 'Staten Island' },
];
function boroughLabel(code) {
  return (BOROUGHS.find((b) => b.code === code) || { label: code }).label;
}
const ROLES = CONTACT_ROLE_OPTIONS;

const STEP_LABELS = ['Basics', 'Targeting', 'Email steps', 'Recipients', 'Review'];

// `initialAccount` ({ id, name }) — when given (deep link from an account's
// Outreach tab via ?new_custom_for=), the wizard builds a custom 1-school
// campaign: name prefilled, targeting pinned to that account via
// filter.account_ids, and is_custom_single_school flagged on the row.
export default function CampaignWizard({ open, onClose, initialAccount = null }) {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [campaign, setCampaign] = useState(null); // populated after step 1
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Step 1 state.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [fromUserId, setFromUserId] = useState('');

  // Step 2 state.
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState({ borough: '', district: '', type: '' });
  // Sprint K — multi-select roles + combine toggle.
  const [contactRoles, setContactRoles] = useState([]);
  const [combineRecipients, setCombineRecipients] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [showAccountList, setShowAccountList] = useState(false);

  // Step 3 state.
  const [steps, setSteps] = useState([]);

  // Step 4 state.
  const [enrollMode, setEnrollMode] = useState('auto'); // 'auto' | 'manual'

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setUsersLoaded(false);
    // Fetch ALL users and filter client-side. The /api/admin/users endpoint
    // takes role=X (single value) — passing comma-separated values
    // generates WHERE role='nexsav_admin,ops' which matches no rows. Plus
    // we don't restrict by email domain anymore: any admin/ops user is a
    // valid sender (Resend surfaces a clear error at send time if the
    // chosen sender's email isn't on a verified domain).
    api.get('/api/admin/users')
      .then((d) => {
        if (!alive) return;
        // Only admin/ops users on the verified-in-Resend nexsav.net domain
        // are valid senders. Picking a gmail/hotmail user here results in
        // Resend 403 ("domain not verified") at send time — the operator
        // should never have that as a footgun option in the picker.
        const list = (d.users || []).filter(
          (u) =>
            (u.role === 'nexsav_admin' || u.role === 'ops') &&
            /@nexsav\.net$/i.test(u.email || ''),
        );
        setUsers(list);
        setUsersLoaded(true);
      })
      .catch(() => { if (alive) setUsersLoaded(true); });
    return () => { alive = false; };
  }, [open]);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setCampaign(null);
    setError('');
    setSubmitting(false);
    setName(initialAccount?.name ? `${initialAccount.name} — custom` : '');
    setDescription(''); setFromUserId('');
    setStages([]);
    // account_ids pins eligibility to the single school; it rides along in
    // every subsequent persistTargeting() PATCH because TargetingStep spreads
    // the existing filter when editing borough/district/type.
    setFilter(initialAccount?.id
      ? { borough: '', district: '', type: '', account_ids: [initialAccount.id] }
      : { borough: '', district: '', type: '' });
    setContactRoles([]); setCombineRecipients(false);
    setEligibility(null); setShowAccountList(false);
    setSteps([]); setEnrollMode('auto');
  }, [open, initialAccount]);

  // ---- Eligibility refresh whenever Targeting fields change.
  // We persist Targeting on the campaign row first (so the existing
  // eligible-accounts endpoint can compute against persisted state),
  // then call the endpoint with include_accounts=1 for the list view.
  const persistTargeting = useCallback(async () => {
    if (!campaign?.id) return;
    await api.patch(`/api/admin/outreach/campaigns/${campaign.id}`, {
      targetLifecycleStagesJson: JSON.stringify(stages),
      targetAccountFilterJson: JSON.stringify(filter),
      // Sprint K — write the new array shape; also keep the legacy single
      // role updated to the first element so any reader that hasn't been
      // migrated yet still sees a sane value.
      targetContactRolesJson: JSON.stringify(contactRoles),
      targetContactRole: contactRoles[0] || null,
      combineRecipients,
      fromUserId: fromUserId || null,
    });
  }, [campaign, stages, filter, contactRoles, combineRecipients, fromUserId]);

  const refreshEligibility = useCallback(async () => {
    if (!campaign?.id) return;
    try {
      await persistTargeting();
      const d = await api.get(`/api/admin/outreach/campaigns/${campaign.id}/eligible-accounts?include_accounts=1&limit=100`);
      setEligibility(d);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to compute eligibility');
    }
  }, [campaign, persistTargeting]);

  // ---- Step 1 → 2: create the draft campaign.
  const submitBasics = async () => {
    setError(''); setSubmitting(true);
    try {
      const res = await api.post('/api/admin/outreach/campaigns', {
        name: name.trim(),
        description: description.trim() || null,
      });
      const c = res.campaign;
      setCampaign(c);
      // Save sender selection if picked, plus the single-school pin for
      // custom campaigns. Keys match the DB_COL allowlist in
      // functions/api/admin/outreach/campaigns/[id].js.
      const patch = {};
      if (fromUserId) patch.fromUserId = fromUserId;
      if (initialAccount?.id) {
        patch.targetAccountFilterJson = JSON.stringify(filter);
        patch.isCustomSingleSchool = true;
      }
      if (Object.keys(patch).length) {
        await api.patch(`/api/admin/outreach/campaigns/${c.id}`, patch);
      }
      setStepIdx(1);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 2 → 3: persist targeting + load eligibility.
  const submitTargeting = async () => {
    setError(''); setSubmitting(true);
    try {
      await refreshEligibility();
      setStepIdx(2);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save targeting');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 3 → 4: hydrate steps for review.
  // (StepEditor mutates `steps` directly — no extra persist needed here.)
  const submitSteps = () => {
    if (!steps.length) {
      setError('Add at least one email step before continuing.');
      return;
    }
    setError(''); setStepIdx(3);
  };

  // ---- Step 4 → 5: persist auto_enroll choice.
  const submitRecipients = async () => {
    setError(''); setSubmitting(true);
    try {
      await api.patch(`/api/admin/outreach/campaigns/${campaign.id}`, {
        autoEnroll: enrollMode === 'auto',
      });
      setStepIdx(4);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save enrollment mode');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 5: activate campaign.
  const activate = async () => {
    setError(''); setSubmitting(true);
    try {
      await api.put(`/api/admin/outreach/campaigns/${campaign.id}`, { status: 'active' });
      onClose?.();
      navigate(`/admin/outreach/${campaign.id}`);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to activate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    if (campaign && stepIdx < 4) {
      // Already created a draft — confirm exit since they'll need to find it again.
      // Soft confirmation; clicking outside still closes.
      // (Detail page panels still let them resume.)
    }
    onClose?.();
    if (campaign) navigate(`/admin/outreach/${campaign.id}`);
  };

  // ---- Render helpers ----------------------------------------------------
  const canAdvance = useMemo(() => {
    if (stepIdx === 0) return name.trim().length > 0 && fromUserId;
    if (stepIdx === 1) return contactRoles.length > 0; // at least one role required
    if (stepIdx === 2) return steps.length > 0;
    return true;
  }, [stepIdx, name, fromUserId, contactRoles, steps]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={null}
      maxWidth="max-w-3xl"
      footer={null}
    >
      <Stepper currentIdx={stepIdx} />
      <div className="px-1">
        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/25 bg-red-500/10 p-2.5 text-[12px] text-red-300">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {stepIdx === 0 && (
          <BasicsStep
            name={name} setName={setName}
            description={description} setDescription={setDescription}
            users={users} usersLoaded={usersLoaded}
            fromUserId={fromUserId} setFromUserId={setFromUserId}
            initialAccount={initialAccount}
          />
        )}

        {stepIdx === 1 && (
          <TargetingStep
            stages={stages} setStages={setStages}
            filter={filter} setFilter={setFilter}
            contactRoles={contactRoles} setContactRoles={setContactRoles}
            combineRecipients={combineRecipients} setCombineRecipients={setCombineRecipients}
            eligibility={eligibility}
            showAccountList={showAccountList} setShowAccountList={setShowAccountList}
            refreshEligibility={refreshEligibility}
          />
        )}

        {stepIdx === 2 && campaign && (
          <StepsStep campaignId={campaign.id} steps={steps} setSteps={setSteps} />
        )}

        {stepIdx === 3 && (
          <RecipientsStep
            enrollMode={enrollMode} setEnrollMode={setEnrollMode}
            eligibility={eligibility}
            campaignId={campaign?.id}
          />
        )}

        {stepIdx === 4 && (
          <ReviewStep
            name={name} description={description}
            fromUser={users.find((u) => u.id === fromUserId)}
            stages={stages} filter={filter}
            contactRoles={contactRoles}
            combineRecipients={combineRecipients}
            stepCount={steps.length}
            enrollMode={enrollMode}
            eligibility={eligibility}
          />
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
          disabled={stepIdx === 0 || submitting || (stepIdx === 1 && !campaign)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="text-[11px] text-gray-500">
          Step {stepIdx + 1} of {STEP_LABELS.length}
        </div>
        {stepIdx === 0 && (
          <button type="button" onClick={submitBasics} disabled={!canAdvance || submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            {submitting ? 'Creating…' : 'Continue'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {stepIdx === 1 && (
          <button type="button" onClick={submitTargeting} disabled={!canAdvance || submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            {submitting ? 'Saving…' : 'Continue'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {stepIdx === 2 && (
          <button type="button" onClick={submitSteps} disabled={!canAdvance}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            Continue <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {stepIdx === 3 && (
          <button type="button" onClick={submitRecipients} disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
            {submitting ? 'Saving…' : 'Continue'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {stepIdx === 4 && (
          <button type="button" onClick={activate} disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 font-semibold disabled:opacity-50">
            {submitting ? 'Activating…' : 'Activate campaign'} <Check className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </Modal>
  );
}

// ---------- Stepper indicator ------------------------------------------
function Stepper({ currentIdx }) {
  return (
    <div className="flex items-center gap-1.5 mb-5 px-1">
      {STEP_LABELS.map((label, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-1.5 ${active ? 'text-white' : done ? 'text-[#B0FC38]' : 'text-gray-600'}`}>
              <div className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] border ${
                active ? 'bg-[#B0FC38] border-[#B0FC38] text-gray-900' :
                done ? 'bg-[#B0FC38]/20 border-[#B0FC38]/40 text-[#B0FC38]' :
                'border-white/[0.1] text-gray-600'
              }`}>
                {done ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className="text-[11px] uppercase tracking-wider">{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`flex-1 h-px ${done ? 'bg-[#B0FC38]/30' : 'bg-white/[0.06]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------- Step 1: Basics --------------------------------------------
function BasicsStep({ name, setName, description, setDescription, users, usersLoaded, fromUserId, setFromUserId, initialAccount }) {
  const noSender = usersLoaded && users.length === 0;
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[14px] font-semibold text-white">Campaign basics</h2>
        <p className="text-[11.5px] text-gray-500 mt-0.5">A short name + the NexSav user this campaign will send from.</p>
      </div>
      {initialAccount?.name && (
        <span
          data-testid="wizard-custom-account-chip"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border bg-[#B0FC38]/10 border-[#B0FC38]/30 text-[#B0FC38]"
        >
          <Users2 className="w-3 h-3" /> Custom campaign for {initialAccount.name}
        </span>
      )}
      <Field label="Name" required>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder="2026-05 Outreach: P.S. principals"
          className={inputCls} />
      </Field>
      <Field label="Description (optional)">
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Internal notes, not visible to recipients."
          className={inputCls} />
      </Field>
      <Field label="Send as" required>
        <select value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} className={inputCls} disabled={noSender}>
          <option value="">{noSender ? 'No sender available' : 'Select a sender…'}</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} &lt;{u.email}&gt; · {u.role.replace('_', ' ')}</option>)}
        </select>
        {noSender ? (
          <div
            data-testid="wizard-no-sender"
            className="mt-1.5 flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-2.5 text-[11.5px] text-amber-300"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              No sender available. Add a staff user with an @nexsav.net email under{' '}
              <Link to="/admin/users" className="underline hover:text-white">Users</Link>.
            </div>
          </div>
        ) : (
          <p className="mt-1 text-[10.5px] text-gray-500">
            Becomes the From: address on every email this campaign sends. Any admin or ops user with a verified email domain.
          </p>
        )}
      </Field>
    </div>
  );
}

// ---------- Step 2: Targeting ------------------------------------------
function TargetingStep({ stages, setStages, filter, setFilter, contactRoles, setContactRoles, combineRecipients, setCombineRecipients, eligibility, showAccountList, setShowAccountList, refreshEligibility }) {
  // Auto-refresh when filters change (debounced via setTimeout).
  useEffect(() => {
    const t = setTimeout(() => { refreshEligibility(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages, filter.borough, filter.district, filter.type, contactRoles, combineRecipients]);

  const toggleRole = (value) => {
    setContactRoles((prev) => prev.includes(value)
      ? prev.filter((r) => r !== value)
      : [...prev, value]);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[14px] font-semibold text-white">Targeting</h2>
        <p className="text-[11.5px] text-gray-500 mt-0.5">Define which accounts this campaign will reach. Eligibility updates live.</p>
      </div>

      <Field label="Lifecycle stages">
        <div className="flex flex-wrap gap-1.5">
          {STAGES.map((s) => {
            const active = stages.includes(s);
            return (
              <button key={s} type="button"
                onClick={() => setStages((prev) => active ? prev.filter((x) => x !== s) : [...prev, s])}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${active ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white' : 'border-white/[0.08] text-gray-400 hover:text-white'}`}>
                {s.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[10.5px] text-gray-500">Pick one or more stages, or leave empty to match accounts at any stage.</p>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Borough">
          <select value={filter.borough} onChange={(e) => setFilter({ ...filter, borough: e.target.value })} className={inputCls}>
            {BOROUGHS.map((b) => <option key={b.code || 'any'} value={b.code}>{b.label}</option>)}
          </select>
        </Field>
        <Field label="District">
          <input value={filter.district} onChange={(e) => setFilter({ ...filter, district: e.target.value })}
            placeholder="9" className={inputCls} />
        </Field>
        <Field label="Account type">
          <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className={inputCls}>
            <option value="">Any</option>
            <option value="school">school</option>
            <option value="external">external</option>
            <option value="district">district</option>
          </select>
        </Field>
      </div>

      <Field label="Recipient roles" required>
        <div className="flex flex-wrap gap-1.5">
          {ROLES.map((r) => {
            const active = contactRoles.includes(r.value);
            return (
              <button key={r.value} type="button"
                onClick={() => toggleRole(r.value)}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${active ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white' : 'border-white/[0.08] text-gray-400 hover:text-white'}`}>
                {r.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[10.5px] text-gray-500">
          Pick one or more roles. The campaign reaches accounts that have at least one contact in any selected role.
        </p>
      </Field>

      <label className={`block rounded-xl border p-3 cursor-pointer transition-colors ${
        combineRecipients ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
      }`}>
        <div className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={combineRecipients}
            onChange={(e) => setCombineRecipients(e.target.checked)}
            className="accent-[#B0FC38] mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] text-white font-medium">
              Send one combined email per account
            </div>
            <div className="text-[10.5px] text-gray-400 mt-0.5 leading-relaxed">
              All matching contacts at each account go into the TO list of a single email. They all share one reply thread. Useful for monthly-update campaigns where the whole school should respond together. {!combineRecipients && <span className="text-gray-500">Off: each enrolled account gets one email to its primary matching contact (current behaviour).</span>}
            </div>
          </div>
        </div>
      </label>

      {/* Live eligibility */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[12.5px] text-gray-200">
            <Users2 className="w-3.5 h-3.5 text-[#B0FC38]" />
            <strong>{eligibility?.total ?? '—'}</strong> eligible account{eligibility?.total === 1 ? '' : 's'}
          </div>
          {eligibility?.accounts && eligibility.accounts.length > 0 && (
            <button type="button" onClick={() => setShowAccountList((v) => !v)}
              className="text-[11px] text-[#B0FC38] hover:text-white">
              {showAccountList ? 'Hide list' : 'Show list'}
            </button>
          )}
        </div>
        {eligibility?.by_borough && Object.keys(eligibility.by_borough).length > 0 && (
          <div className="text-[11px] text-gray-500 mt-1.5">
            <MapPin className="inline w-3 h-3 mr-1" />
            {Object.entries(eligibility.by_borough).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </div>
        )}
        {showAccountList && eligibility?.accounts && (
          <div className="mt-3 max-h-48 overflow-y-auto space-y-1 pr-1">
            {eligibility.accounts.map((a) => (
              <div key={a.id} className="text-[11.5px] text-gray-300 flex items-center justify-between gap-2 px-2 py-1 rounded bg-white/[0.02]">
                <span className="truncate">{a.name}</span>
                <span className="text-[10px] text-gray-500 shrink-0">
                  {a.borough || '—'}{a.district ? `·${a.district}` : ''} · {a.lifecycleStage}
                </span>
              </div>
            ))}
            {eligibility.total > eligibility.accounts.length && (
              <div className="text-[10.5px] text-gray-500 text-center pt-1">
                …and {eligibility.total - eligibility.accounts.length} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Step 3: Email steps ----------------------------------------
function StepsStep({ campaignId, steps, setSteps }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-[14px] font-semibold text-white">Email steps</h2>
        <p className="text-[11.5px] text-gray-500 mt-0.5">
          Add one or more emails. These send in order, with per-step delays. At least one is required.
        </p>
      </div>
      <StepEditor campaignId={campaignId} steps={steps} onChange={setSteps} />
    </div>
  );
}

// ---------- Step 4: Recipients -----------------------------------------
function RecipientsStep({ enrollMode, setEnrollMode, eligibility, campaignId }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[14px] font-semibold text-white">Enrollment</h2>
        <p className="text-[11.5px] text-gray-500 mt-0.5 leading-relaxed">
          <span className="text-gray-300">Targeting (step 2) defines the eligible pool</span>: the accounts a campaign is relevant to.
          This step is just <span className="text-gray-300">how</span> accounts from that pool get added: automatically as they qualify, or one-by-one when you're ready.
          Both modes are constrained to the targeting pool. You can flip between them later from the enrollment page.
        </p>
      </div>

      <label className={`block rounded-xl border p-4 cursor-pointer transition-colors ${
        enrollMode === 'auto' ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
      }`}>
        <div className="flex items-start gap-3">
          <input type="radio" name="enroll-mode" value="auto" checked={enrollMode === 'auto'}
            onChange={() => setEnrollMode('auto')} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white font-medium">Auto-enroll matching accounts</div>
            <div className="text-[11.5px] text-gray-400 mt-1 leading-relaxed">
              {eligibility?.total ?? 0} account{eligibility?.total === 1 ? '' : 's'} in the pool right now will be enrolled the next time the auto-enroll cron runs (or immediately if you click "Run now" later). New accounts that match in the future are added automatically.
              <br /><span className="text-gray-500">Best for: ongoing campaigns like monthly newsletters where everyone in the pool should always be included.</span>
            </div>
          </div>
        </div>
      </label>

      <label className={`block rounded-xl border p-4 cursor-pointer transition-colors ${
        enrollMode === 'manual' ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
      }`}>
        <div className="flex items-start gap-3">
          <input type="radio" name="enroll-mode" value="manual" checked={enrollMode === 'manual'}
            onChange={() => setEnrollMode('manual')} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white font-medium">Manual selection from the pool</div>
            <div className="text-[11.5px] text-gray-400 mt-1 leading-relaxed">
              You'll see the {eligibility?.total ?? 0} eligible account{eligibility?.total === 1 ? '' : 's'} on the enrollment page and pick which ones to enroll. Nothing happens until you click Enroll.
              <br /><span className="text-gray-500">Best for: controlled rollouts (send to 5 first, monitor, then enroll the rest).</span>
            </div>
            {enrollMode === 'manual' && campaignId && (
              <button type="button" onClick={() => navigate(`/admin/outreach/${campaignId}/enroll`)}
                className="mt-2 text-[11px] text-[#B0FC38] hover:text-white">
                Open enrollment picker now →
              </button>
            )}
          </div>
        </div>
      </label>
    </div>
  );
}

// ---------- Step 5: Review ---------------------------------------------
function ReviewStep({ name, description, fromUser, stages, filter, contactRoles, combineRecipients, stepCount, enrollMode, eligibility }) {
  const filterPieces = [];
  if (filter.borough) filterPieces.push(`borough: ${boroughLabel(filter.borough)}`);
  if (filter.district) filterPieces.push(`district ${filter.district}`);
  if (filter.type) filterPieces.push(`type: ${filter.type}`);
  if (filter.account_ids?.length) filterPieces.push(`pinned to ${filter.account_ids.length} account${filter.account_ids.length === 1 ? '' : 's'}`);

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-[14px] font-semibold text-white">Review &amp; activate</h2>
        <p className="text-[11.5px] text-gray-500 mt-0.5">When you click Activate, the campaign goes live and starts sending per the rules below.</p>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-2.5 text-[12px] text-gray-300">
        <Row label="Name" value={name} />
        {description && <Row label="Description" value={description} />}
        <Row label="Send as" value={fromUser ? `${fromUser.name} <${fromUser.email}>` : '—'} />
        <Row label="Stages" value={stages.length ? stages.join(', ').replace(/_/g, ' ') : 'any'} />
        <Row label="Filter" value={filterPieces.length ? filterPieces.join(', ') : 'no geo/type filter'} />
        <Row label="Recipient roles" value={contactRoles.length ? contactRoles.map(contactRoleLabel).join(', ') : '—'} />
        <Row label="Send mode" value={combineRecipients
          ? 'Combined: one email per account to all matching contacts'
          : 'Per contact: one email to the primary matching contact'} />
        <Row label="Email steps" value={`${stepCount} step${stepCount === 1 ? '' : 's'}`} />
        <Row label="Eligible accounts" value={`${eligibility?.total ?? 0} now`} />
        <Row label="Enrollment mode" value={enrollMode === 'auto' ? 'Auto-enroll (cron + manual run-now)' : 'Manual only'} />
      </div>
      <p className="text-[11px] text-gray-500">
        You can edit any of these from the campaign detail page after activation. Run the cron manually any time with the "Run now" button.
      </p>
    </div>
  );
}

// ---------- Shared bits ------------------------------------------------
const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white placeholder-gray-600';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
        {label}{required && <span className="text-red-400/80 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3">
      <span className="text-[10.5px] uppercase tracking-wider text-gray-500">{label}</span>
      <span className="text-gray-200 break-words">{value}</span>
    </div>
  );
}
